import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Lenis from 'lenis';
import "@/App.css";
import HomePage from './pages/HomePage';
import TemplatesGallery from './pages/TemplatesGallery';
import TemplateDetail from './pages/TemplateDetail';
import TemplatePreview from './pages/TemplatePreview';

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.4,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const isRTL = i18n.language === 'ar';
  const SITE_URL = 'https://horizondigitalagency.org';
  const OG_IMAGE = 'https://customer-assets.emergentagent.com/job_growth-system-18/artifacts/zh7iujff_Swiss%20Delight%20-%20Chocolate%20%26%20Cake%20Shop%20Theme.jpg';

  return (
    <Router>
      <div className={`App min-h-screen bg-[#FAF8F5] ${isRTL ? 'rtl' : ''}`} data-testid="app-container" dir={isRTL ? 'rtl' : 'ltr'}>
        <Helmet>
          <title>Horizon Digital Agency | Website Design & SEO Services</title>
          <meta name="description" content="We design, rebuild, and optimize websites to help businesses grow and get more clients. Professional web design, SEO optimization, and branding services. Starting from $400." />
          <meta name="keywords" content="web design agency, website design services, website redesign, SEO optimization, branding agency, custom website development, landing page design, e-commerce website, responsive web design, affordable web design, web design Dubai, web design London, web design Paris, web design Morocco, web design Casablanca, agence web design, diseno web" />
          <meta name="author" content="Horizon Digital Agency" />
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <link rel="canonical" href={SITE_URL} />
          <meta property="og:title" content="Horizon Digital Agency | Website Design & SEO Services" />
          <meta property="og:description" content="We design high-converting websites that turn visitors into paying clients. Custom web design, SEO, and branding for businesses worldwide. Starting from $400." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={SITE_URL} />
          <meta property="og:image" content={OG_IMAGE} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:site_name" content="Horizon Digital Agency" />
          <meta property="og:locale" content="en_US" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Horizon Digital Agency | Website Design & SEO Services" />
          <meta name="twitter:description" content="Professional web design starting from $400. 50+ projects delivered. 98% client satisfaction." />
          <meta name="twitter:image" content={OG_IMAGE} />
          <link rel="alternate" hrefLang="en" href={`${SITE_URL}/`} />
          <link rel="alternate" hrefLang="fr" href={`${SITE_URL}/?lang=fr`} />
          <link rel="alternate" hrefLang="es" href={`${SITE_URL}/?lang=es`} />
          <link rel="alternate" hrefLang="ar" href={`${SITE_URL}/?lang=ar`} />
          <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />
          <html lang={i18n.language} dir={isRTL ? 'rtl' : 'ltr'} />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org", "@type": "LocalBusiness", "@id": SITE_URL,
            "name": "Horizon Digital Agency", "description": "We design, rebuild, and optimize websites to help businesses grow.", "url": SITE_URL, "email": "contact@horizondigitalagency.org", "image": OG_IMAGE, "priceRange": "$300 - $1,500",
            "address": { "@type": "PostalAddress", "addressCountry": "MA" },
            "areaServed": [{ "@type": "Country", "name": "United Kingdom" }, { "@type": "Country", "name": "United Arab Emirates" }, { "@type": "Country", "name": "France" }, { "@type": "Country", "name": "Spain" }, { "@type": "Country", "name": "Morocco" }],
            "serviceType": ["Web Design", "Website Redesign", "SEO Optimization", "Branding & Design", "Landing Page Design"],
            "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": "50", "bestRating": "5" }
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "How long does it take to build a website?", "acceptedAnswer": { "@type": "Answer", "text": "Most projects are delivered within 1-2 weeks. Landing pages can be ready in 3-5 days." }},
              { "@type": "Question", "name": "Do you offer revisions?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Starter includes 1 round, Standard 2, Premium unlimited." }},
              { "@type": "Question", "name": "Can you redesign my existing website?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. We specialize in redesigning outdated websites." }},
              { "@type": "Question", "name": "Do you offer ongoing SEO services?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Monthly SEO packages starting at $150/month." }}
            ]
          })}</script>
        </Helmet>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/templates" element={<TemplatesGallery />} />
          <Route path="/templates/:id" element={<TemplateDetail />} />
          <Route path="/templates/:id/preview" element={<TemplatePreview />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
